
import { useState, useEffect, useRef } from 'react';
import { workSteps } from '../../../mocks/workSteps';

export default function HowWeWork() {
  const [headerVisible, setHeaderVisible] = useState(false);
  const [stepsVisible, setStepsVisible] = useState(false);
  const [whyUsVisible, setWhyUsVisible] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const stepsRef = useRef<HTMLDivElement>(null);
  const whyUsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const obs1 = new IntersectionObserver(([e]) => { if (e.isIntersecting) setHeaderVisible(true); }, { threshold: 0.1 });
    const obs2 = new IntersectionObserver(([e]) => { if (e.isIntersecting) setStepsVisible(true); }, { threshold: 0.05 });
    const obs3 = new IntersectionObserver(([e]) => { if (e.isIntersecting) setWhyUsVisible(true); }, { threshold: 0.05 });
    if (headerRef.current) obs1.observe(headerRef.current);
    if (stepsRef.current) obs2.observe(stepsRef.current);
    if (whyUsRef.current) obs3.observe(whyUsRef.current);
    return () => { obs1.disconnect(); obs2.disconnect(); obs3.disconnect(); };
  }, []);

  const whyUsFeatures = [
    { icon: 'ri-shield-check-line', title: 'Гарантия качества', description: 'До 12 месяцев гарантии на все виды работ', color: 'from-teal-500 to-emerald-500' },
    { icon: 'ri-timer-line', title: 'Быстрый выезд', description: 'Приедем в течение 1-2 часов после звонка', color: 'from-emerald-500 to-teal-600' },
    { icon: 'ri-user-settings-line', title: 'Опытные мастера', description: 'Более 5 лет опыта ремонта любой техники', color: 'from-teal-600 to-emerald-600' },
    { icon: 'ri-money-dollar-circle-line', title: 'Честные цены', description: 'Прозрачное ценообразование без скрытых платежей', color: 'from-emerald-600 to-teal-500' },
  ];

  return (
    <section id="how-we-work" className="py-14 md:py-24 bg-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, rgb(51, 65, 85) 1px, transparent 0)', backgroundSize: '40px 40px' }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header — scale in */}
        <div ref={headerRef} className={`text-center mb-12 md:mb-20 transition-all duration-800 ${headerVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'}`} style={{ transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)' }}>
          <h2 className="text-3xl md:text-5xl font-bold mb-3 md:mb-4 bg-gradient-to-r from-slate-900 via-teal-800 to-slate-900 bg-clip-text text-transparent">
            Как мы работаем
          </h2>
          <div className={`h-1 mx-auto rounded-full bg-gradient-to-r from-teal-500 to-emerald-500 transition-all duration-1000 delay-300 ${headerVisible ? 'w-24' : 'w-0'}`}></div>
          <p className={`text-sm md:text-xl text-slate-600 max-w-2xl mx-auto mt-3 md:mt-4 transition-all duration-700 delay-200 ${headerVisible ? 'opacity-100' : 'opacity-0'}`}>
            Простой и прозрачный процесс от заявки до завершения ремонта
          </p>
        </div>

        {/* Steps */}
        <div className="relative max-w-5xl mx-auto mb-14 md:mb-24" ref={stepsRef}>
          {/* Desktop timeline line */}
          <div className={`absolute left-1/2 top-0 bottom-0 w-0.5 -translate-x-1/2 hidden md:block transition-all duration-1500 ${stepsVisible ? 'bg-slate-200' : 'bg-transparent'}`} style={{ transitionDelay: '200ms' }}></div>
          {/* Mobile timeline line */}
          <div className={`absolute left-6 top-0 bottom-0 w-0.5 md:hidden transition-all duration-1500 ${stepsVisible ? 'bg-slate-200' : 'bg-transparent'}`}></div>

          <div className="space-y-8 md:space-y-16">
            {workSteps.map((step, index) => (
              <div
                key={step.id ?? index}
                className={`relative transition-all duration-800 ${
                  stepsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-16'
                }`}
                style={{ transitionDelay: `${index * 250}ms`, transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)' }}
              >
                {/* Mobile layout */}
                <div className="flex gap-4 md:hidden">
                  <div className="relative z-10 flex-shrink-0">
                    <div className={`w-12 h-12 rounded-full bg-gradient-to-br from-teal-500 to-emerald-500 flex items-center justify-center shadow-lg transition-all duration-700 ${stepsVisible ? 'scale-100 rotate-0' : 'scale-0 rotate-90'}`} style={{ transitionDelay: `${index * 250 + 150}ms` }}>
                      <i className={`${step.icon} text-lg text-white`}></i>
                    </div>
                  </div>
                  <div className={`flex-1 bg-white rounded-xl p-4 shadow-md border border-gray-100 transition-all duration-700 ${stepsVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`} style={{ transitionDelay: `${index * 250 + 100}ms` }}>
                    <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-teal-500 to-emerald-500 text-white font-bold text-sm mb-2">
                      {step.step}
                    </div>
                    <h3 className="text-base font-bold text-slate-900 mb-1.5">{step.title}</h3>
                    <p className="text-xs text-slate-600 leading-relaxed">{step.description}</p>
                  </div>
                </div>

                {/* Desktop layout — alternating slide from left/right */}
                <div className={`hidden md:flex items-center gap-8 ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
                  <div className="flex-1 group">
                    <div className={`bg-gradient-to-br from-white to-slate-50 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:scale-105 relative overflow-hidden ${
                      stepsVisible
                        ? 'opacity-100 translate-x-0'
                        : index % 2 === 0 ? 'opacity-0 -translate-x-20' : 'opacity-0 translate-x-20'
                    }`} style={{ transitionDelay: `${index * 250 + 100}ms`, transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)' }}>
                      <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-teal-500 via-emerald-500 to-teal-500 opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10 blur-xl"></div>
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-500 text-white font-bold text-lg mb-4 group-hover:scale-110 group-hover:rotate-12 transition-all duration-500">
                        {step.step}
                      </div>
                      <h3 className="text-2xl font-bold text-slate-900 mb-3 group-hover:text-teal-600 transition-colors duration-300">
                        {step.title}
                      </h3>
                      <p className="text-slate-600 leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                  <div className="relative z-10 flex-shrink-0">
                    <div className={`w-16 h-16 md:w-20 md:h-20 rounded-full bg-gradient-to-br from-teal-500 via-emerald-500 to-teal-600 flex items-center justify-center shadow-xl transition-all duration-700 ${stepsVisible ? 'scale-100 rotate-0' : 'scale-0 rotate-180'}`} style={{ transitionDelay: `${index * 250 + 200}ms`, transitionTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)' }}>
                      <i className={`${step.icon} text-2xl md:text-3xl text-white`}></i>
                    </div>
                  </div>
                  <div className="flex-1"></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Why Choose Us */}
        <div ref={whyUsRef}>
          <div className={`text-center mb-8 md:mb-12 transition-all duration-800 ${whyUsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-10'}`}>
            <h3 className="text-2xl md:text-4xl font-bold mb-2 md:mb-4 bg-gradient-to-r from-slate-900 via-teal-800 to-slate-900 bg-clip-text text-transparent">
              Почему выбирают нас
            </h3>
            <p className="text-sm md:text-lg text-slate-600">Преимущества работы с профессионалами</p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
            {whyUsFeatures.map((feature, index) => (
              <div
                key={index}
                className={`group transition-all duration-700 ${
                  whyUsVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-12 scale-90'
                }`}
                style={{ transitionDelay: `${300 + index * 150}ms`, transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)' }}
              >
                <div className="relative h-full bg-white rounded-xl md:rounded-2xl p-4 md:p-6 shadow-md md:shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-1 md:hover:-translate-y-2 overflow-hidden cursor-pointer">
                  <div className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-10 transition-opacity duration-500`}></div>
                  <div className={`w-10 h-10 md:w-14 md:h-14 rounded-lg md:rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-3 md:mb-4 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500`}>
                    <i className={`${feature.icon} text-lg md:text-2xl text-white`}></i>
                  </div>
                  <h4 className="text-sm md:text-xl font-bold text-slate-900 mb-1 md:mb-2 group-hover:text-teal-600 transition-colors duration-300">
                    {feature.title}
                  </h4>
                  <p className="text-xs md:text-sm text-slate-600 leading-relaxed">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
